<template>
  <v-snackbar
    :contained="contained"
    content-class="base-snackbar-text bg-white"
    location="top center"
    :model-value="modelValue"
    :style="
      offsetLeft != null
        ? {
            justifyContent: 'flex-start',
            '--base-snackbar-offset-left': `${offsetLeft}px`
          }
        : undefined
    "
    :timeout="timeout"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="d-flex align-center ga-3 bg-white" :data-test-id="testId">
      <v-icon
        v-if="icon"
        :color="iconColor"
        :data-test-id="testId && `${testId}-icon`"
        :icon="icon"
        size="small"
      />

      <slot />
    </div>

    <template #actions>
      <slot name="actions" />

      <v-btn
        :aria-label="i18n.t('buttons.close')"
        class="close-button text-black"
        :data-test-id="testId && `${testId}-close`"
        elevation="0"
        max-height="24"
        max-width="24"
        variant="text"
        @click="emit('update:modelValue', false)"
      >
        <v-icon size="12"> $close </v-icon>
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

withDefaults(
  defineProps<{
    modelValue: boolean;
    icon?: string | null;
    iconColor?: string;
    timeout?: number;
    testId?: string;
    contained?: boolean;
    offsetLeft?: number | null;
  }>(),
  {
    icon: undefined,
    iconColor: 'primary',
    timeout: -1,
    testId: undefined,
    contained: false,
    offsetLeft: null
  }
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void;
}>();

const i18n = useI18n();
</script>

<style lang="scss" scoped>
.base-snackbar-text {
  span {
    display: block;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    letter-spacing: -0.005em;
    color: black;
  }
}

// By default VSnackbar stretches its content to fill the row and wraps
// the actions (button/close) onto their own line once the message text is
// too wide for the wrapper. Keep everything on a single row by letting the
// wrapper grow to fit its content (instead of wrapping/clipping at the
// default max-width) and keeping the content/actions at their natural
// (non-shrinking) width so the text can't overflow into the actions.
:deep(.v-snackbar__wrapper) {
  flex-wrap: nowrap;
  width: max-content;
  max-width: 90vw;
  // Consumers that need precise, dynamically-computed horizontal placement
  // (e.g. centering within a sub-region of a `contained` overlay's box, not
  // the whole box) can supply `offsetLeft`, which pairs with the root's
  // `justify-content: flex-start` override above to position the wrapper
  // exactly `offsetLeft`px from the box's left edge instead of relying on
  // `justify-content: center` across the box's full width.
  margin-left: var(--base-snackbar-offset-left, auto);
}

:deep(.v-snackbar__content) {
  flex: 0 0 auto;
  margin-right: 0;
}

:deep(.v-snackbar__actions) {
  flex: 0 0 auto;
}

// Vuetify pads `.v-snackbar` by `var(--v-layout-*)` on all four sides (the
// app-bar height, plus any permanent nav-drawers' widths) so non-contained
// snackbars stay clear of them, even when `contained`. For a contained
// banner this padding is unwanted on every side, not just the top:
// - Its positioned ancestor already sits below the header, so the top
//   padding doubles that offset and pushes it too far down.
// - Consumers that compute their own horizontal `offsetLeft` (like
//   `ReplaceSignatureBanner`) already account for surrounding nav-drawers
//   by measuring the actual visible viewport directly, so on top of that,
//   Vuetify's own `padding-left`/`padding-right: var(--v-layout-left|right)`
//   would double-apply the same drawer-width compensation and push the
//   banner off-center.
// Note: no `:deep()` here — `.v-snackbar` is the root node rendered by the
// `<v-snackbar>` child component, and Vue forwards this component's scope
// attribute onto that root node directly, so `:deep()` (a descendant
// combinator) would never match it.
.v-snackbar.v-overlay--contained {
  padding: 0;
}

.close-button {
  border-radius: 9999px;

  &:hover {
    background-color: rgb(var(--v-theme-grey-lighten-1));
  }
}
</style>
